import { Form, FormSelect, FormTextInput, StandardFormButtons } from '@components/form';
import { Tab, Tabs } from '@components/tabs';
import { useCreateMcpServerMutation, useGetWorkspacesNameListQuery } from '@generated/graphql';
import { MenuItem, Stack } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useNotifications } from '@notifications/Notifications';
import { toBackendFormat, type ParsedMcpServer } from '@utils/mcp-config';
import posthog from 'posthog-js';
import { useState, type SyntheticEvent } from 'react';
import * as yup from 'yup';
import { McpServerConfigField } from './McpServerConfigField';
import { McpServerDescriptionField } from './McpServerDescriptionField';
import { mcpServerConfigSchema, mcpServerDescriptionSchema, mcpServerNameSchema } from './utils';

const validationSchema = yup.object({
  scopeType: yup.string().required('Scope type is required'),
  workspaceId: yup.string(),
  description: mcpServerDescriptionSchema,
  config: mcpServerConfigSchema,
  manualName: mcpServerNameSchema.notRequired(),
});

type CreateMcpServerConfigModalValues = {
  scopeType: 'organization' | 'user' | 'workspace';
  workspaceId?: string;
  description?: string;
  config: ParsedMcpServer[];
  manualName: string;
};

type CreateMcpServerConfigModalProps = {
  fixedScopeType?: 'organization' | 'user' | 'workspace';
  fixedWorkspaceId?: string;
  onCreate?: VoidFunction;
  onClose: VoidFunction;
};

export const CreateMcpServerConfigModal = ({
  fixedScopeType,
  fixedWorkspaceId,
  onCreate,
  onClose,
}: CreateMcpServerConfigModalProps) => {
  const { createAsyncNotification } = useNotifications();
  const { organizationId } = useOrganizationContext();
  const [createMcpServer] = useCreateMcpServerMutation();
  const [formError, setFormError] = useState<string | null>(null);

  const { data: workspacesData } = useGetWorkspacesNameListQuery({
    variables: { organizationId },
  });
  const workspaces = workspacesData?.getWorkspaces || [];

  const [tab, setTab] = useState<'import' | 'manual'>('import');
  const handleTabChange = (_event: SyntheticEvent, newValue: unknown) => {
    setTab(newValue as 'import' | 'manual');
  };

  const handleSubmit = createAsyncNotification(
    'MCP servers created successfully.',
    async ({
      scopeType,
      workspaceId,
      manualName,
      description,
      config,
    }: CreateMcpServerConfigModalValues) => {
      const creates = config.map(async (newServer) => {
        posthog.capture('agent-studio:user_create-mcp-server_submitted');
        return createMcpServer({
          variables: {
            scopeType,
            organizationId: scopeType === 'organization' ? organizationId : undefined,
            workspaceId: scopeType === 'workspace' ? workspaceId : undefined,
            name: tab === 'import' ? newServer.name : manualName,
            config: toBackendFormat(newServer),
            description,
            enabled: scopeType !== 'organization',
          },
          onCompleted: (data) => {
            posthog.capture('agent-studio:user_create-mcp-server_completed', {
              scopeType,
              organizationId,
              workspaceId,
              mcpServerId: data?.createMcpServer?.mcpServerId,
            });
          },
          onError: (error) => {
            posthog.capture('agent-studio:user_create-mcp-server_failed', {
              scopeType,
              organizationId,
              workspaceId,
            });
            setFormError(error.message);
          },
        });
      });

      await Promise.all(creates);
      onCreate?.();
      onClose();
    },
    {
      loadingMessage: 'Creating MCP servers...',
    },
  );

  return (
    <Form<CreateMcpServerConfigModalValues>
      initialValues={{
        scopeType: fixedScopeType || 'organization',
        workspaceId: fixedWorkspaceId || '',
        manualName: '',
        description: '',
        config: [],
      }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ values }) => {
        return (
          <Stack sx={{ width: 550 }}>
            <FormSelect name="scopeType" label="Scope type" disabled={!!fixedScopeType}>
              <MenuItem value="organization">Organization</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="workspace">Workspace</MenuItem>
            </FormSelect>
            {values.scopeType === 'workspace' && (
              <FormSelect name="workspaceId" label="Workspace" disabled={!!fixedWorkspaceId}>
                {workspaces.map((workspace) => (
                  <MenuItem key={workspace.workspaceId} value={workspace.workspaceId}>
                    {workspace.name}
                  </MenuItem>
                ))}
              </FormSelect>
            )}
            <Tabs value={tab} onChange={handleTabChange} variant="fullWidth" sx={{ mb: 2 }}>
              <Tab value="import" label="Import snippet" />
              <Tab value="manual" label="Manual entry" />
            </Tabs>
            {tab === 'manual' && <FormTextInput name="manualName" label="Name" />}
            <McpServerDescriptionField name="description" />
            <McpServerConfigField
              name="config"
              organizationId={organizationId}
              workspaceId={
                fixedWorkspaceId || values.scopeType === 'workspace'
                  ? values.workspaceId
                  : undefined
              }
              showExamples={tab === 'import'}
            />
            <StandardFormButtons onClose={onClose} formError={formError} />
          </Stack>
        );
      }}
    </Form>
  );
};
